import React, { useCallback, useState, useMemo, useEffect } from 'react';
import JSON5 from 'json5';
import styled from 'styled-components';
import { uniq } from 'lodash';
import { SectionBase } from './SectionBase';
import Selector, {
  MAX_WIDTH_FOR_SELECTOR,
  SelectorOptions,
} from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  Indicator,
  INDICATOR_STAGES,
  IndicatorGroup,
} from './ImpactIndicator/utils';
import { Answer } from './Review/utils';
import { getReachIndicators, GuidanceTextResponse } from './utils';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';

const StyledDiv = styled.div`
  margin: ${SPACERS.medium} auto ${SPACERS.extraLarge};
  padding-top: ${SPACERS.large};
  border-top: 1px solid ${COLORS.greyBase};
`;

export const CapacityDevelopmentHelpText: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);

  const handleToggle = useCallback(() => {
    setOpen(o => !o);
  }, [setOpen]);

  return (
    <>
      <div style={{ textAlign: 'left', marginBottom: SPACERS.tiny }}>
        <TrueImpactText variant="heading-subheading" color="ctaBrandOrange">
          This is to include the <strong>Program Development Stage</strong> in
          your impact model (if relevant).
        </TrueImpactText>
        <TrueImpactText>
          Each included indicator you select will need to be defined,
          quantified, and measured.
        </TrueImpactText>
        <TrueImpactText style={{ marginTop: SPACERS.small }}>
          <a onClick={handleToggle}>
            Not sure what to select? Review additional guidance here:
          </a>
        </TrueImpactText>
        <TrueImpactCollapse show={open}>
          <div>
            <TrueImpactText
              variant="body-bold"
              style={{ paddingTop: SPACERS.small }}
            >
              When to include a Program Development indicator:
            </TrueImpactText>
            <ul style={{ marginTop: SPACERS.tiny, marginBottom: SPACERS.tiny }}>
              <li>
                If funding received for this program year went towards specific
                organizational improvements.
              </li>
              <li>
                If significant organizational improvements were made that are
                important to your wholistic story of impact.
              </li>
            </ul>
            <TrueImpactText variant="body-bold">
              When NOT to include Program Development indicators:
            </TrueImpactText>
            <ul style={{ marginTop: SPACERS.tiny, marginBottom: SPACERS.tiny }}>
              <li>
                If it was a standard operational year with no substantial
                organizational or programmatic changes, we encourage you to
                select “N/A”.
              </li>
            </ul>
            <TrueImpactText>
              We recommend no more than 3 Program Development indicators for a
              simplified model. Please be mindful that the more indicators you
              include, the longer this process can take.
            </TrueImpactText>
          </div>
        </TrueImpactCollapse>
      </div>
    </>
  );
};
export const CAPACITY_DEVELOPMENT_NONE_TEXT =
  'N/A: None of these are relevant to the work during this reporting period.';
const otherText = CAPACITY_DEVELOPMENT_NONE_TEXT;

const parseValue = (value: string | string[] | undefined = []): string[] => {
  try {
    return typeof value === 'string'
      ? JSON5.parse(value.replace(/'/g, '"'))
      : value;
  } catch (error) {
    return [];
  }
};

const defaultGetLabel = (beneficiaryTerm?: string) => (value: string) =>
  value === 'People are reached' && beneficiaryTerm !== 'Animals'
    ? `${beneficiaryTerm} are reached`
    : value;

interface SectionCheckboxesProps {
  answers: Answer[];
  title: string;
  subTitle: string;
  helpText: string;
  causeCategory?: string;
  serviceName?: string;
  beneficiaryTerm?: string;
  indicators: IndicatorGroup[];
  questionId: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  options: string[];
  guidanceText: GuidanceTextResponse;
  value: string | string[] | undefined;
  onChange: (questionId: number, value: string | string[]) => unknown;
}

export const SectionCheckboxes: React.FC<SectionCheckboxesProps> = ({
  title,
  subTitle,
  helpText,
  questionId,
  onChange,
  error,
  options,
  indicators,
  guidanceText,
  causeCategory,
  serviceName,
  beneficiaryTerm,
  answers,
  value,
}) => {
  const {
    reachQuestionId,
    subServiceQuestionId,
    capacityDevelopmentQuestionId,
    setIndicatorState,
  } = useIndicator();
  const selectedGroup = useMemo(
    () => answers.find(x => x.questionId === subServiceQuestionId)?.text,
    [answers],
  );

  const [selected, setSelected] = useState<string[]>(parseValue(value));

  useEffect(() => {
    setSelected(parseValue(value));
  }, [questionId]);

  const handleChange = useCallback(
    (value: string) => {
      setSelected(s => {
        const newState = [...s];
        const index = newState.indexOf(value);
        if (index === -1) {
          newState.push(value);
        } else {
          newState.splice(index, 1);
        }
        setIndicatorState(questionId, newState);
        onChange(questionId, newState);
        return newState;
      });
    },
    [questionId, setSelected, onChange],
  );

  const handleOtherClick = useCallback(() => {
    const newState: string[] = [];
    setSelected(newState);
    setIndicatorState(questionId, newState);
    onChange(questionId, newState);
  }, [questionId, setSelected, onChange]);

  const reachIndicators = useMemo(
    () =>
      questionId === reachQuestionId
        ? getReachIndicators(
            causeCategory,
            serviceName,
            beneficiaryTerm,
            indicators,
          )
        : [],
    [
      questionId,
      reachQuestionId,
      causeCategory,
      serviceName,
      beneficiaryTerm,
      indicators,
    ],
  );

  const humanReachNames = useMemo(
    () => new Set(reachIndicators.filter(r => r.isHuman).map(r => r.name)),
    [reachIndicators],
  );

  const hasSelectedHumanReach = useMemo(
    () =>
      questionId === reachQuestionId &&
      selected.some(s => humanReachNames.has(s)),
    [questionId, reachQuestionId, selected, humanReachNames],
  );

  const selectOptions: string[] = useMemo(() => {
    if (questionId === reachQuestionId) {
      return reachIndicators.map(r => r.name);
    }

    if (questionId !== capacityDevelopmentQuestionId) return uniq(options);

    const result: string[] = [];
    const selectedCapacityDevelopment: string[] = [];
    const stageId = INDICATOR_STAGES.capacityDevelopment;

    for (const group of indicators) {
      for (const indicator of group.indicators) {
        if (indicator.stageId === stageId) {
          if (group.name === selectedGroup) {
            selectedCapacityDevelopment.push(indicator.name);
          }
          result.push(indicator.name);
        }
      }
    }
    return uniq(
      selectedCapacityDevelopment.length > 0
        ? selectedCapacityDevelopment
        : result,
    );
  }, [
    options,
    questionId,
    indicators,
    reachQuestionId,
    capacityDevelopmentQuestionId,
    reachIndicators,
    selectedGroup,
  ]);

  const isCapacityDevelopmentQuestion =
    questionId === capacityDevelopmentQuestionId;

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={
        isCapacityDevelopmentQuestion ? (
          <CapacityDevelopmentHelpText />
        ) : (
          helpText
        )
      }
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      <SelectorOptions
        variant="checkbox"
        options={selectOptions.length === 0 ? selected : selectOptions}
        getLabel={defaultGetLabel(beneficiaryTerm)}
        getSelected={val => selected.includes(val)}
        getDisabled={val =>
          hasSelectedHumanReach &&
          humanReachNames.has(val) &&
          !selected.includes(val)
        }
        onClick={handleChange}
        tooltip={val =>
          indicators
            .map(x => x.indicators.find(y => y.name === val))
            .find(Boolean)?.definition || ''
        } // indicator.name => indicator.definition
      />
      {isCapacityDevelopmentQuestion && (
        <StyledDiv>
          <Selector
            variant="radio"
            label={otherText}
            selected={Boolean(value) && selected.length === 0}
            maxWidth={MAX_WIDTH_FOR_SELECTOR}
            onClick={handleOtherClick}
          />
        </StyledDiv>
      )}
    </SectionBase>
  );
};
